/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import debounce from 'lodash.debounce';
import {getMigrationStatus} from '@ion/core/src/services/Reports';
import {SisenseState} from '@ion/core/src/services/SisenseUtils';
import {GenericObject} from '@ion/core/src/types/entities';
import {AgGridType} from '@ion/dls/src/grid/IonGrid';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import useLoadGridData from '@ion/dls/src/grid/useLoadGridData';
import QueryKey from '@ion/query/src/config/QueryKeys';
import usePaginatedQueryCache from '@ion/query/src/hooks/usePaginatedQueryCache';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import useSisenseSelector from '../../sisense/hooks/useSisenseSelector';
import {migrationResultGridFilterModel} from './store';

/**
 * Interface representing the results returned by the `useLoadMigrationResults` hook.
 * 
 * @property {Function} getMigrationStatus - Method to load migration events based on the provided parameters.
 * @property {FilterModel} filterModel - The filter model applied to the migration events grid. This should be
 *                                        updated with the actual type of the filter model if known.
 * @property {boolean} refresh - A boolean indicating whether a refresh is currently in progress.
 * @property {UsePaginationResult} paging - Object containing pagination information and methods.
 * @property {boolean} fetching - A boolean indicating whether data fetching is currently in progress.
 * @property {Function} onRefresh - Function to manually trigger a refresh of the data.
 * @property {GenericObject} apiParams - Object containing the API parameters used to fetch migration events.
 */
export interface UseLoadMigrationResultsResults {
  getAllMigrationResults: (params) => void;
  filterModel: FilterModel;
  refresh: boolean;
  paging: UsePaginationResult;
  fetching: boolean;
  onRefresh: Function;
  apiParams: GenericObject;
}

interface UseLoadMigrationResultsProps {
  gridApi: AgGridType;
  globalAccountId: number;
  viewSubGroups: boolean;
  searchValue: string;
}

/**
 * The React hook for loading migration events into an AgGrid instance with pagination support.
 * It handles fetching migration event data based on filter criteria and provides functions for refreshing data.
 * 
 * ```typescript
 * 
 *  import {useLoadMigrationResults} from './useLoadMigrationResults';
 * 
 *  const options = useLoadMigrationResults(gridApi, 9001, false);
 * 
 * ```
 * 
 * @param {AgGridType} gridApi - The AgGrid instance representing the grid.
 * @param {number} globalAccountId - The global account ID to be used for fetching migration events.
 * @param {boolean} viewSubGroups - Flag to indicate whether to include sub-groups in the data fetching.
 * 
 * @returns {UseLoadMigrationResultsResults} An object containing methods and values for loading and managing 
 *                                          migration event data.
 */
export const useLoadMigrationResults = ({
  gridApi,
  globalAccountId,
  viewSubGroups,
  searchValue
}: UseLoadMigrationResultsProps): UseLoadMigrationResultsResults => {
  const sisenseOptions      = useSisenseSelector(SisenseState.ServerOptions);
  const sisenseOptionsError = useSisenseSelector(SisenseState.SisenseOptionsError);
  const sessionCreated      = useSisenseSelector(SisenseState.SessionCreated);
  const isEnabled           = !!sessionCreated && !sisenseOptionsError && !!sisenseOptions;
  const filterModel = migrationResultGridFilterModel.value;

  const apiParams = useMemo(() => ({
    filterOn         : searchValue,
    accountId        : globalAccountId,
    includeSubGroups : viewSubGroups
  }), [globalAccountId, viewSubGroups, searchValue]);

  const {
    loadItems,
    refresh,
    paging,
    fetching,
    onRefresh
  } = usePaginatedQueryCache({
    api             : getMigrationStatus,
    baseKey         : QueryKey.MIGRATION_RESULTS,
    initialPageSize : 20,
    parseKey        : 'migrationResults',
    apiParams,
    enabled         : isEnabled,
    enabledPrefetch : isEnabled
  });

  const getReports = useCallback(debounce(({page, ...params}) => {
    return loadItems({
      ...apiParams,
      ...params,
      searchTerms : {
        ...apiParams.searchTerms,
        ...params?.filter
      },
      pageId : page + 1
    });
  }, 350), [loadItems, apiParams, searchValue]);

  const getAllMigrationResults = useCallback(useLoadGridData({
    loadData : getReports,
    filterModel,
    gridApi
  }), [getReports, filterModel, gridApi, searchValue]);

  return {
    getAllMigrationResults,
    filterModel,
    refresh,
    paging,
    fetching,
    onRefresh,
    apiParams
  };
};

export default useLoadMigrationResults;
